import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, FolderInput, Loader2, Disc3, Info } from "lucide-react";
import { api } from "@/lib/api";
import { UploadZone } from "@/components/riptag/UploadZone";
import { appConfirm } from "@/components/AppDialogHost";

export const ImportModal = ({ onClose, onDone }) => {
  const [sources, setSources] = useState(null);
  const [importing, setImporting] = useState("");

  const loadSources = () => {
    api.get("/music/import/sources").then((r) => setSources(r.data.sessions || [])).catch(() => setSources([]));
  };
  useEffect(loadSources, []);

  const needSession = async () => (await api.post("/music/upload/session")).data.session_id;

  const onUploaded = async (sid) => {
    try {
      const r = await api.post("/music/import/riptag", { session_id: sid });
      toast.success(`${r.data.added} track${r.data.added === 1 ? "" : "s"} added to your library`);
    } catch { toast.error("Upload finished but adding to the library failed"); }
    loadSources();
    onDone();
  };

  const importRip = async (sessionId, name) => {
    if (!sessionId && !(await appConfirm(
      `This imports every ready track from all ${ready.length} Rip & Tag session${ready.length === 1 ? "" : "s"} into your library.`,
      { title: "Import everything?" }))) return;
    setImporting(sessionId || "all");
    try {
      const r = await api.post("/music/import/riptag", { session_id: sessionId });
      toast.success(`${r.data.added} track${r.data.added === 1 ? "" : "s"} imported${name ? ` from "${name}"` : " from Rip & Tag"}${r.data.needs_review ? ` · ${r.data.needs_review} duplicate${r.data.needs_review === 1 ? "" : "s"} marked for review` : ""}`);
      loadSources();
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    setImporting("");
  };

  const ready = (sources || []).filter((s) => s.ready > 0 && !s.organizer);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}
      data-testid="ml-import-modal">
      <div className="w-full max-w-2xl rounded-xl border border-line bg-bg-surface p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="type-section-heading text-ink-primary">Import Music</h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-primary" data-testid="ml-import-close"><X size={16} /></button>
        </div>

        <UploadZone sessionId="" onNeedSession={needSession} onUploaded={onUploaded} />

        <div className="mt-5">
          <div className="type-status-badge font-mono uppercase tracking-wider text-ink-muted mb-2">
            <Disc3 size={11} className="inline mr-1 -mt-0.5" />// Sync from Rip &amp; Tag Studio
          </div>
          {sources === null ? (
            <div className="type-body text-ink-muted"><Loader2 size={13} className="inline animate-spin mr-1" /> Checking sessions…</div>
          ) : ready.length === 0 ? (
            <div className="type-body text-ink-muted" data-testid="ml-rip-empty">
              All Rip &amp; Tag tracks are already in your library (duplicate-flagged tracks stay out automatically).
            </div>
          ) : (
            <div className="space-y-1.5">
              {ready.map((s) => (
                <div key={s.session_id} className="flex items-center gap-3 rounded-md border border-line/40 px-3 py-2">
                  <FolderInput size={14} className="text-ink-muted shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="type-list-title text-ink-primary truncate">{s.name}</div>
                    <div className="type-list-subtitle text-ink-muted">{s.ready} track{s.ready === 1 ? "" : "s"} ready</div>
                  </div>
                  <button onClick={() => importRip(s.session_id, s.name)} disabled={!!importing}
                    data-testid={`ml-rip-import-${s.session_id}`}
                    className="type-button-label rounded-md border border-line px-2.5 py-1 text-ink-primary hover:bg-white/5 disabled:opacity-40">
                    {importing === s.session_id ? <Loader2 size={12} className="animate-spin" /> : "Import"}
                  </button>
                </div>
              ))}
              <button onClick={() => importRip("", "")} disabled={!!importing} data-testid="ml-rip-import-all"
                className="type-button-label w-full rounded-md border border-amber-500/40 px-3 py-2 text-brand-amber hover:bg-amber-500/10 disabled:opacity-40">
                {importing === "all" ? <Loader2 size={12} className="inline animate-spin" /> : "Import everything that's ready"}
              </button>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-start gap-2 rounded-md border border-line/40 px-3 py-2">
          <Info size={12} className="text-ink-muted mt-0.5 shrink-0" />
          <p className="type-timestamp text-ink-muted">
            Browsers can't scan arbitrary PC folders — full local-library scanning arrives with the desktop companion.
            You can still drop a whole album folder here, or use "Browse album folder".
          </p>
        </div>
      </div>
    </div>
  );
};
